import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Shield, Users, ChartLineUp, 
  Brain, Check,
  Play, Heart, Sparkle, CheckCircle, Phone, Desktop,
  Cpu, Lightning, Target, TrendingUp
} from "@phosphor-icons/react";

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: ChartLineUp,
      title: "Hospice Operations Optimization",
      description: "Build high-performing teams and scalable operations. From census growth strategy to RN workload modeling and staffing optimization.",
      gradient: "from-cyan-500 to-blue-600",
    },
    {
      icon: Shield,
      title: "Compliance & Regulatory Implementation",
      description: "Turn regulations into operational systems. Survey readiness, HOPE implementation, QAPI design, and compliance leadership training.",
      gradient: "from-indigo-500 to-purple-600",
    },
    {
      icon: Brain,
      title: "AI Workflow Design & Intelligence",
      description: "Practical AI-enabled systems that improve visibility, reduce administrative burden, and support clinical decision-making.",
      gradient: "from-emerald-500 to-teal-600",
    },
  ];

  const stats = [
    { number: "30", label: "Census Growth in 90 Days", suffix: "%+" },
    { number: "98", label: "Client Satisfaction Rate", suffix: "%" },
    { number: "0", label: "Audit Deficiencies", suffix: "" },
    { number: "24", label: "Hour Response Time", suffix: "hr" },
  ];

  const advantages = [
    "Integrated strategy across operations, compliance, and technology",
    "Decades of real-world hospice leadership experience",
    "AI-enabled systems designed for healthcare workflows",
    "Free strategy consultation to assess your needs"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section - Futuristic */}
      <section className="min-h-screen relative flex items-center bg-slate-50" data-testid="hero-section">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-50" />
        
        {/* Animated gradient orbs */}
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-1/4 w-80 h-80 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, transparent 70%)' }}
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-7xl mx-auto container-padding py-20 md:py-32 lg:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.span 
                className="badge badge-accent mb-8 inline-flex items-center"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                data-testid="hero-badge"
              >
                <Cpu weight="fill" className="w-4 h-4 mr-2" />
                AI-Powered Hospice Consulting
              </motion.span>
              
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
                Transform Your{" "}
                <span className="text-gradient">Healthcare</span>{" "}
                Operations
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl mb-10">
                Empowering hospice organizations with intelligent systems, 
                regulatory expertise, and operational excellence—from strategy to execution.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link
                  to="/contact"
                  className="btn-accent inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-primary"
                >
                  Get Started
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Link>
                <a
                  href="tel:+17183046214"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-phone"
                >
                  <Phone weight="fill" className="w-5 h-5" />
                  +1 (718) 304-6214
                </a>
              </div>
              
              {/* Trust indicators - Redesigned */}
              <div className="flex items-center gap-8 flex-wrap">
                {["HIPAA Compliant", "CMS CoP Aligned", "CHAP Support"].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                      <Check weight="bold" className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-slate-600 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Main Image with Futuristic Frame */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 rounded-3xl blur-lg opacity-30" />
                <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZCUyMHRlY2hub2xvZ3klMjBibHVlfGVufDB8fHx8MTc3MzAzMTQ3MHww&ixlib=rb-4.1.0&q=85"
                    alt="Healthcare technology and data analytics"
                    className="w-full aspect-[4/5] object-cover"
                    data-testid="hero-image"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                </div>
              </div>
              
              {/* Floating Stats Card */}
              <motion.div 
                className="absolute -bottom-8 -left-8 glass-card p-6 rounded-2xl shadow-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                    <CheckCircle weight="fill" className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="font-display font-bold text-slate-900 text-lg">Zero Deficiencies</p>
                    <p className="text-sm text-slate-500">Client audit success rate</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Growth Card */}
              <motion.div 
                className="absolute -top-6 -right-6 glass-card p-5 rounded-2xl shadow-xl"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              >
                <div className="text-center">
                  <p className="text-4xl font-display font-bold text-gradient">30%+</p>
                  <p className="text-xs text-slate-500 font-medium mt-1">Census Growth</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - Dark & Futuristic */}
      <section className="py-20 bg-slate-900 relative overflow-hidden" data-testid="stats-section">
        {/* Animated background lines */}
        <div className="absolute inset-0 opacity-20">
          <div className="data-line top-1/4" />
          <div className="data-line top-1/2" />
          <div className="data-line top-3/4" />
        </div>
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                variants={itemVariants}
                data-testid={`stat-${index}`}
              >
                <div className="stat-number mb-2">
                  {stat.number}<span className="text-3xl">{stat.suffix}</span>
                </div>
                <div className="text-slate-400 text-sm font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* About Section - Clean Modern */}
      <section className="section-padding bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-3xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1739932885175-5fdaa1bd5989?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxob3NwaWNlJTIwY2FyZSUyMGNvbXBhc3Npb24lMjBlbGRlcmx5fGVufDB8fHx8MTc3MzAzMTQ2NHww&ixlib=rb-4.1.0&q=85"
                alt="Compassionate healthcare - hands holding"
                className="relative rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-accent mb-6">About Noble Healthcare</span>
              <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-8 leading-tight tracking-tight">
                Dedicated to Your Hospice's Growth
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We support healthcare organizations with practical, real-world solutions 
                that strengthen leadership and improve day-to-day operations. With deep 
                experience in hospice and clinical care, we provide QAPI guidance, enhance 
                IDG processes, and deliver growth strategies tailored to your needs.
              </p>
              
              <div className="space-y-4 mb-10">
                {advantages.map((item, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check weight="bold" className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/about"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="about-link"
              >
                Learn More About Us
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section className="section-padding bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Our Services</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              Comprehensive Healthcare Solutions
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From operations optimization to AI-powered systems, we deliver 
              integrated solutions that transform hospice organizations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
              >
                <div className={`feature-icon mb-6 bg-gradient-to-r ${service.gradient} group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon weight="duotone" className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900 mb-4">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/services"
              className="btn-accent inline-flex items-center gap-2"
              data-testid="services-link"
            >
              View All Services
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Products Section - Modern Cards */}
      <section className="section-padding bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Our Products</span>
            <h2 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight">
              AI-Powered Tools for Excellence
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Purpose-built software solutions designed by hospice experts to streamline 
              compliance, automate reporting, and ensure survey readiness.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* HospiceGuard */}
            <motion.div
              className="relative rounded-3xl p-8 text-white overflow-hidden group"
              style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="product-hospiceguard"
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="absolute top-4 right-4 w-24 h-24 border border-cyan-500/20 rounded-full" />
              <div className="absolute bottom-4 left-4 w-16 h-16 border border-cyan-500/20 rounded-full" />
              
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-6">
                  <Shield weight="fill" className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">HospiceGuard</h3>
                <p className="text-cyan-400 text-lg mb-4">Mobile Hospice Audit System</p>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  HIPAA-compliant mobile audit solution with AI-powered documentation review. 
                  Conduct thorough mock audits and track compliance in real-time.
                </p>
                <a
                  href="https://goodwinguard.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-6 py-3 rounded-full hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                >
                  Learn More
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </a>
              </div>
            </motion.div>

            {/* Incident Insight */}
            <motion.div
              className="relative rounded-3xl p-8 text-white overflow-hidden group"
              style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="product-incidentinsight"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="absolute top-4 right-4 w-24 h-24 border border-indigo-500/20 rounded-full" />
              <div className="absolute bottom-4 left-4 w-16 h-16 border border-indigo-500/20 rounded-full" />
              
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center mb-6">
                  <Lightning weight="fill" className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">Incident Insight Pro</h3>
                <p className="text-indigo-400 text-lg mb-4">AI-Powered Incident Reporting</p>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  HIPAA-compliant incident reporting with automated Root Cause Analysis. 
                  Streamline documentation and transform incidents into quality improvements.
                </p>
                <a
                  href="https://incidentpath.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold px-6 py-3 rounded-full hover:shadow-lg hover:shadow-indigo-500/30 transition-all"
                >
                  Learn More
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="products-link"
            >
              View All Products
              <ArrowRight weight="bold" className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Futuristic */}
      <section className="cta-section section-padding" data-testid="cta-section">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 border border-cyan-500/30 rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-500/30 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-cyan-500/20 rounded-full" />
        </div>
        
        {/* Gradient orbs */}
        <motion.div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mx-auto mb-8">
              <Sparkle weight="fill" className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-tight">
              Ready to Transform Your Hospice?
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
              Partner with us to improve patient care, grow census, and maintain 
              regulatory excellence through intelligent, integrated solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/30 hover:-translate-y-1 inline-flex items-center justify-center gap-2"
                data-testid="cta-consultation"
              >
                Book Free Consultation
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
              <Link
                to="/schedule-demo"
                className="border-2 border-slate-600 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-slate-800 hover:border-slate-500 inline-flex items-center justify-center gap-2"
                data-testid="cta-demo"
              >
                <Desktop weight="fill" className="w-5 h-5" />
                Schedule a Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
